import {
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { DatePicker } from '@blocksuite/affine-components/date-picker';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import { IS_MOBILE } from '@blocksuite/global/env';
import { flip, offset, shift } from '@floating-ui/dom';
import { computed, signal } from '@preact/signals-core';
import { html } from 'lit';

import {
  normalizeDateRange,
  type NormalizedDateRange,
} from '../../core/logical/date-value.js';
import { BaseCellRenderer } from '../../core/property/index.js';
import { createFromBaseCellRenderer } from '../../core/property/renderer.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import {
  dateCellStyle,
  dateValueContainerStyle,
} from '../date/cell-renderer-css.js';
import {
  dateRangeFooterStyle,
  dateRangeMobileFooterStyle,
  dateRangeMobilePickerStyle,
  dateRangeMobileToggleStyle,
  dateRangePopoverStyle,
  dateRangeToggleStyle,
} from './cell-renderer-css.js';
import { dateRangePropertyModelConfig, formatDateRange } from './define.js';

export class DateRangeCell extends BaseCellRenderer<unknown, unknown> {
  private _prevPortalAbortController: AbortController | null = null;

  private readonly openDatePicker = () => {
    if (
      this._prevPortalAbortController &&
      !this._prevPortalAbortController.signal.aborted
    )
      return;

    this.tempValue$.value = normalizeDateRange(this.value) ?? undefined;

    this._prevPortalAbortController?.abort();
    const abortController = new AbortController();
    abortController.signal.addEventListener(
      'abort',
      () => {
        this.selectCurrentCell(false);
      },
      { once: true }
    );
    this._prevPortalAbortController = abortController;

    const setupDatePicker = () => {
      const datePicker = new DatePicker();
      const current = this.tempValue$.value;
      // unlike the `date` type there is no Range toggle: this column is a span
      datePicker.range = true;
      datePicker.value = current?.start ?? Date.now();
      if (current) {
        datePicker._rangeStart = new Date(current.start);
        datePicker._rangeEnd = new Date(current.end);
      }
      datePicker.showTime = current?.hasTime ?? false;

      datePicker.onClear = () => {
        this.tempValue$.value = undefined;
      };
      // fired while only one endpoint has been picked
      datePicker.onChange = (date: Date) => {
        this.tempValue$.value = {
          start: date.getTime(),
          end: date.getTime(),
          hasTime: datePicker.showTime,
        };
      };
      datePicker.onChangeRange = ({ start, end, hasTime }) => {
        this.tempValue$.value = {
          start: start.getTime(),
          end: end?.getTime() ?? start.getTime(),
          hasTime: !!hasTime,
        };
      };
      datePicker.onEscape = () => {
        abortController.abort();
      };

      const toggleTime = (e: Event) => {
        const checked = (e.target as HTMLInputElement).checked;
        datePicker.showTime = checked;
        const current = this.tempValue$.value;
        if (current) this.tempValue$.value = { ...current, hasTime: checked };
      };

      return { datePicker, toggleTime };
    };

    if (IS_MOBILE) {
      popMenu(popupTargetFromElement(this), {
        options: {
          title: {
            text: this.property.name$.value,
          },
          onClose: () => {
            abortController.abort();
          },
          items: [
            () =>
              html` <div class="${dateValueContainerStyle}">
                ${this.formattedTempValue$.value}
              </div>`,
            () => {
              const { datePicker, toggleTime } = setupDatePicker();
              datePicker.padding = 0;
              requestAnimationFrame(() => datePicker.focusDateCell());
              return html` <div class="${dateRangeMobilePickerStyle}">
                ${datePicker}
                <div class="${dateRangeMobileFooterStyle}">
                  <label class="${dateRangeMobileToggleStyle}"
                    ><input
                      type="checkbox"
                      .checked=${datePicker.showTime}
                      @change=${toggleTime}
                    />
                    Time</label
                  >
                </div>
              </div>`;
            },
          ],
        },
      });
    } else {
      const { portal } = createLitPortal({
        abortController,
        closeOnClickAway: true,
        computePosition: {
          referenceElement: this,
          placement: 'bottom',
          middleware: [offset(10), flip(), shift({ padding: 8 })],
        },
        template: () => {
          const { datePicker, toggleTime } = setupDatePicker();
          datePicker.popup = true;
          requestAnimationFrame(() => datePicker.focusDateCell());
          return html`<div class="${dateRangePopoverStyle}">
            ${datePicker}
            <div class="${dateRangeFooterStyle}">
              <label class="${dateRangeToggleStyle}"
                ><input
                  type="checkbox"
                  .checked=${datePicker.showTime}
                  @change=${toggleTime}
                />
                Time</label
              >
            </div>
          </div>`;
        },
      });
      portal.style.zIndex = '1002';
    }
  };

  private readonly updateValue = () => {
    const tempValue = this.tempValue$.value;
    const currentValue = normalizeDateRange(this.value);

    if (!tempValue && !currentValue) {
      return;
    }
    if (
      tempValue &&
      currentValue &&
      tempValue.start === currentValue.start &&
      tempValue.end === currentValue.end &&
      tempValue.hasTime === currentValue.hasTime
    ) {
      return;
    }

    this.valueSetNextTick(tempValue ? { ...tempValue } : null);
    this.tempValue$.value = undefined;
  };

  tempValue$ = signal<NormalizedDateRange | undefined>();

  formattedTempValue$ = computed(() =>
    formatDateRange(this.tempValue$.value ?? null)
  );

  formattedValue$ = computed(
    () =>
      this.formattedTempValue$.value ||
      formatDateRange(normalizeDateRange(this.value))
  );

  override afterEnterEditingMode() {
    this.openDatePicker();
  }

  override beforeExitEditingMode() {
    this.updateValue();
    this._prevPortalAbortController?.abort();
  }

  override render() {
    return html` <div class="${dateCellStyle} date-range">
      ${this.formattedValue$.value}
    </div>`;
  }
}

export const dateRangePropertyConfig =
  dateRangePropertyModelConfig.createPropertyMeta({
    icon: createIcon('CalendarPanelIcon'),
    cellRenderer: {
      view: createFromBaseCellRenderer(DateRangeCell),
    },
  });
